import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Switch from 'react-switch';
import PropTypes from 'prop-types';
import commaNumber from 'comma-number';
import cogoToast from 'cogo-toast';
import _ from 'lodash';
import cn from 'classnames';
import moment from 'moment';
import './Forecast.scss';

// React picker
import 'react-dates/initialize';
import { SingleDatePicker } from 'react-dates';
import 'react-dates/lib/css/_datepicker.css';

import CheckIcon from '@material-ui/icons/Check';
import KeyboardArrowUp from '@material-ui/icons/KeyboardArrowUp';

import ForecastRow from '../components/Forecast/ForecastRow';

import { hasHubspotConnected } from '../helpers/user_helpers';

const Forecast = (props) => {
  const { getAllDeals, deals, user } = props;
  const { allDeals } = deals;
  const [hideClosedDeals, setHideClosedDeals] = useState(true);

  // New Deal Fields
  const [newCloseDateFocused, setNewCloseDateFocused] = useState(false);
  const [newCloseDate, setNewCloseDate] = useState(null);
  const [newName, setName] = useState(null);
  const [newCloseProb, setCloseProb] = useState(null);
  const [newDealSize, setDealSize] = useState(null);

  useEffect(() => {
    window.__ADD_EVENT__('Deals - Page View');
  }, []);

  const updateCloseProb = (newVal) => {
    setCloseProb(newVal.replace(/[^0-9]+/g, '').slice(0, 3));
  };

  const updateDealSize = (newVal) => {
    setDealSize(newVal.replace(/[^0-9]+/g, ''));
  };

  const filteredDeals = _.filter(allDeals, (deal) => {
    if (hideClosedDeals) return !deal.is_closed;
    return true;
  });
  const dealSectionsObj = _.groupBy(filteredDeals, (deal) => moment(deal.close_date).format('MMM YYYY'));
  const dealSections = _.orderBy(_.values(dealSectionsObj), 'close_date');

  const getProjRevFromDeal = (deal) => deal.close_probability * deal.deal_size || 0;
  const getProjRevFromDeal_CB = (deal) => deal.calculated_close_probability * deal.deal_size || 0;
  const getProjRevDiffForDeal = (deal) => ((getProjRevFromDeal_CB(deal) - getProjRevFromDeal(deal)) * 100) / getProjRevFromDeal(deal) || 0;
  const getProjRevFromDeals = (deals) => _.sum(_.map(deals, getProjRevFromDeal));
  const getProjRevFromDeals_CB = (deals) => _.sum(_.map(deals, getProjRevFromDeal_CB));
  const getProjRevDiffForDeals = (deals) => ((getProjRevFromDeals_CB(deals) - getProjRevFromDeals(deals)) * 100) / getProjRevFromDeals(deals) || 0;

  const getColumns = (deals) => {
    const projRevFromDeals = getProjRevFromDeals(deals).toFixed(0);
    const projRevFromDealsCB = getProjRevFromDeals_CB(deals).toFixed(0);
    const projDiffFromDeals = parseFloat(getProjRevDiffForDeals(deals).toFixed(0));
    const newProjRev = (_.min([parseFloat(newCloseProb), 100]) * parseFloat(newDealSize)) / 100 || 0;
    return [
      {
        display: 'Company',
        totalDisplay: `${moment(deals[0].close_date).format('MMMM')} Projections`,
        classNames: 'company',
        inputField: <input className='company' placeholder='Company Name' value={newName} onChange={(e) => setName(e.target.value)} />,
        displayForDeal: (deal) => deal.name,
      },
      {
        display: 'Close Probability',
        totalDisplay: null,
        classNames: 'close-prob sm',
        inputField: (
          <input
            className='close-prob'
            placeholder='Close %'
            value={newCloseProb && `${newCloseProb}%`}
            onChange={(e) => updateCloseProb(e.target.value)}
          />
        ),
        displayForDeal: (deal) => (deal.close_probability ? (deal.close_probability * 100).toFixed(0) + '%' : '-'),
      },
      {
        display: 'CB Close Probability',
        totalDisplay: null,
        classNames: 'close-prob-cb sm',
        displayForDeal: (deal) => (deal.calculated_close_probability ? (deal.calculated_close_probability * 100).toFixed(0) + '%' : '-'),
      },
      {
        display: 'Deal Size',
        totalDisplay: `$${commaNumber(_.sum(_.map(deals, (d) => parseInt(d.deal_size))))}`,
        classNames: 'deal-size md',
        inputField: (
          <input
            className='deal-size'
            placeholder='Deal Size'
            value={newDealSize && `$${commaNumber(newDealSize)}`}
            onChange={(e) => updateDealSize(e.target.value)}
          />
        ),
        displayForDeal: (deal) => (deal.deal_size ? '$' + commaNumber(deal.deal_size) : '-'),
      },
      {
        display: 'Projected Revenue',
        totalDisplay: `$${commaNumber(projRevFromDeals)}`,
        classNames: 'proj-revenue md',
        inputField: newProjRev ? <div className='new-proj-rev'>${commaNumber(newProjRev.toFixed(0))}</div> : null,
        displayForDeal: (deal) => (getProjRevFromDeal(deal) ? `$${commaNumber(getProjRevFromDeal(deal).toFixed(0))}` : '-'),
      },
      {
        display: 'CB Projected Revenue',
        totalDisplay: (
          <>
            ${commaNumber(projRevFromDealsCB)}
            {!!projDiffFromDeals && (
              <span className={cn('diff', { good: projDiffFromDeals >= 0.5, bad: projDiffFromDeals <= -0.5 })}>
                {projDiffFromDeals > 0 ? '+' : '-'}
                {Math.abs(projDiffFromDeals)}%
              </span>
            )}
          </>
        ),
        classNames: `proj-revenue-cb md`,
        displayForDeal: (deal) => {
          const projRev = getProjRevFromDeal_CB(deal);
          const projDiffFromDeal = parseFloat(getProjRevDiffForDeal(deal).toFixed(0));
          return projRev ? (
            <div className={cn({ closed: deal.is_closed })}>
              ${commaNumber(projRev.toFixed(0))}
              {deal.is_closed ? (
                <CheckIcon />
              ) : (
                !!projDiffFromDeal && (
                  <span className={cn('diff', { good: projDiffFromDeal >= 0.5, bad: projDiffFromDeal <= -0.5 })}>
                    {projDiffFromDeal > 0 ? '+' : '-'}
                    {Math.abs(projDiffFromDeal)}%
                  </span>
                )
              )}
            </div>
          ) : (
            '-'
          );
        },
      },
      {
        display: 'Close Date',
        totalDisplay: null,
        classNames: 'close-date sm',
        inputField: (
          <SingleDatePicker
            date={newCloseDate && moment(newCloseDate)}
            onDateChange={(date) => setNewCloseDate(date)}
            focused={newCloseDateFocused}
            onFocusChange={({ focused }) => setNewCloseDateFocused(focused)}
            id='close-date-selector'
            placeholder='Close Date'
            withPortal
            noBorder
            displayFormat='MMM Do'
          />
        ),
        displayForDeal: (deal) => moment(deal.close_date || deal.close_date).format('MMM Do'),
      },
      {
        display: 'Most Recent Call',
        totalDisplay: null,
        classNames: 'recent-call sm',
        displayForDeal: (deal) => deal.recent_chat && moment(deal.recent_chat.created_at).format('MMM Do'),
      },
    ];
  };

  const canCreateDeal = newName?.length && newCloseProb?.length && newDealSize?.length;

  const submitDeal = (fallbackMonth) => {
    if (!newCloseDate) return cogoToast.warn('Please specify a close date.');
    if (!newName) return cogoToast.warn('Please define a company name.');
    if (!newCloseProb) return cogoToast.warn('Please estimate a close probability.');
    if (!newDealSize) return cogoToast.warn('Please specify a deal size.');

    props
      .addDeal({
        close_date: moment(newCloseDate).format('YYYY-MM-DD hh:mm'), // .endOf('month')
        name: newName,
        close_probability: parseFloat(newCloseProb) / 100,
        deal_size: parseFloat(newDealSize),
      })
      .then((resp) => {
        if (resp.deal) {
          setNewCloseDate('');
          setName('');
          setCloseProb('');
          setDealSize('');
          window.__ADD_EVENT__('Deals - Add Deal', { deal: resp.deal });
        }
      });
  };

  useEffect(() => {
    getAllDeals();
  }, [getAllDeals]);

  return (
    <div className='forecast-outer-container'>
      <div className='forecast-inner-container'>
        {!hasHubspotConnected(user) && !dealSections.length ? (
          <div className='empty-deals-view'>
            <div>You first must connect your HubSpot account on your{` `}</div>
            <Link to='/account'>account page</Link>
          </div>
        ) : (
          <>
            <div className='header-section'>
              <div className='header'>Projected Deals</div>
              <div className='filters'>
                <div className='toggle-closed-container'>
                  HIDE CLOSED DEALS
                  <Switch
                    onChange={() => setHideClosedDeals(!hideClosedDeals)}
                    checked={hideClosedDeals}
                    height={14}
                    width={24}
                    onColor='#333'
                    offColor='#ccc'
                    className='switch'
                    checkedIcon={false}
                    uncheckedIcon={false}
                  />
                </div>
              </div>
            </div>
            {dealSections.map((deals, idx) => {
              const firstDeal = deals[0];
              const month = moment(firstDeal.close_date).format('MMMM');
              const columns = getColumns(deals);
              return (
                <div key={idx} className='section'>
                  <div className='section-header'>{month}</div>
                  <div className='row header'>
                    <div className='main-data header'>
                      {columns.map((col, idx) => (
                        <div key={idx} className={`cell ${col.classNames}`}>
                          {col.display}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className='section-body'>
                    {deals.map((deal) => (
                      <ForecastRow key={deal.id} deal={deal} columns={columns} deleteDeal={props.deleteDeal} />
                    ))}
                    <div className='row new'>
                      <form
                        onSubmit={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          submitDeal(moment(firstDeal.close_date));
                        }}
                        className='main-data'
                      >
                        {columns.map((col, idx) => (
                          <div key={idx} className={`cell ${col.classNames}`}>
                            {col.inputField}
                          </div>
                        ))}
                        {
                          <button className={cn('create-deal-btn', { active: canCreateDeal })} type='submit'>
                            Create Deal
                          </button>
                        }
                      </form>
                    </div>
                    <div className='row total'>
                      <div className='main-data'>
                        {columns.map((col, idx) => (
                          <div key={idx} className={`cell ${col.classNames}`}>
                            {col.totalDisplay}
                          </div>
                        ))}
                        <KeyboardArrowUp className='toggle-expand empty' />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
};

Forecast.propTypes = {
  user: PropTypes.object.isRequired,
  ui: PropTypes.object.isRequired,
  deals: PropTypes.object.isRequired,
  getAllDeals: PropTypes.func.isRequired,
  addDeal: PropTypes.func.isRequired,
  deleteDeal: PropTypes.func.isRequired,
  updateDeal: PropTypes.func.isRequired,
};

export default Forecast;
